// ============================================================
// Mutasyon → Firestore belgesi izdüşümü
//
// SORUN
// Uygulama yazarken yalnızca mutasyon günlüğüne yazıyordu. Kiracı
// ağacı ise Firestore'da ayrıca BELGE olarak duruyor (tohum betiği
// onları yazmıştı). İkisi ilk yazmadan sonra ayrışıyordu:
//   - tenants/.../students/{id} belgesi hiç oluşmuyordu
//   - limitleriDenetle / personelDegisti tetiklenmiyordu
//   - kiracı belgesindeki usage toplamları donuyor, sistem paneli
//     bayat sayı gösteriyordu
//
// ÇÖZÜM
// Her mutasyondan sonra, etkilenen BELGENİN o anki hâlini bellekteki
// ağaçtan alıp Firestore'a yazıyoruz. Alan alan fark hesaplamıyoruz;
// belgeyi bütün olarak yazmak fazladan bir yazma ama kendi kendini
// onarır: bir yazma kaçsa bile sonraki mutasyon belgeyi düzeltir.
//
// KAPSAM
// Yalnızca Firestore'da gerçekten belge olan koleksiyonlar. Yoklama,
// mesaj, ödeme gibi kayıtlar bellekte şubenin alanları, Firestore'da
// ise ayrı alt koleksiyonlar; onları buradan yazmak şube belgesini
// şişirir ve kurallarla çelişir. Onlar günlükten oynatılmaya devam
// ediyor.
// ============================================================

import { getAt } from "./path.js";

// Bellekteki ağaç ile Firestore belgeleri birebir örtüşen
// koleksiyonlar. Bellekte şubenin altında DİZİ, Firestore'da alt
// koleksiyon; kimlik ikisinde de aynı.
//
// Buraya eklenmeyen diziler (messages, applications, expenses …)
// hâlâ yalnızca mutasyon günlüğünde. Uygulama içinde çalışıyorlar
// ama sunucu onları göremiyor — sunucudan okunması gereken bir iş
// çıkarsa önce buraya eklenmeleri gerekir.
const BELGE_KOLEKSIYONLARI = new Set([
  "students", "staff", "classes", "incidents", "medications",
]);

// Şube belgesinin skaler alanları. Şubenin altındaki diziler
// (students, messages …) ASLA yazılmaz — onlar ayrı koleksiyon ve
// buraya kopyalanırsa belge şişer, kural da reddeder.
//
// Yoklama istisna: ayrı bir alt koleksiyona (attendance/{gun}/records)
// yazılıyor, aşağıda ele alınıyor.
const SUBE_ALANLARI = ["name", "address", "phone", "serviceFee"];

/**
 * Yolun baştaki (koleksiyon, {id}) çiftlerini ayıklar.
 * ["tenants",{id},"branches",{id},"students",{id},"allergies"]
 *   → [["tenants","x"],["branches","y"],["students","z"]]  (kalan: ["allergies"])
 */
function ciftler(yol) {
  const parcalar = [];
  let i = 0;
  while (
    i + 1 < yol.length &&
    typeof yol[i] === "string" &&
    yol[i + 1] && typeof yol[i + 1] === "object" && "id" in yol[i + 1]
  ) {
    parcalar.push([yol[i], yol[i + 1].id]);
    i += 2;
  }
  return { parcalar, kalan: yol.slice(i) };
}

/**
 * Bir mutasyonun Firestore'a yansıması.
 *
 * @returns {null | {yol: string, veri: object, birlestir?: boolean} | {yol: string, sil: true}}
 */
export function projeksiyonCikar(kok, mutasyon) {
  const { parcalar, kalan } = ciftler(mutasyon.path ?? []);
  if (!parcalar.length) return null;

  const [koleksiyon] = parcalar.at(-1);
  const belgeYolu = parcalar.map(([k, id]) => `${k}/${id}`).join("/");

  // Belgenin tamamı silindi mi? (yolda artık alan adı yok)
  if (mutasyon.op === "delete" && !kalan.length) {
    if (!BELGE_KOLEKSIYONLARI.has(koleksiyon)) return null;
    return { yol: belgeYolu, sil: true };
  }

  // Diziye YENİ KAYIT eklenmesi. Yol henüz kaydın kendisini
  // göstermiyor ([..., "staff"]) çünkü kimlik push edilen nesnede.
  //
  // Bu bir süre eksikti ve sonucu ağırdı: uygulamadan eklenen
  // personelin Firestore belgesi HİÇ oluşmuyordu. Kayıt bellekte ve
  // mutasyon günlüğünde vardı, uygulama içinde çalışıyordu; ama
  // sunucu onu göremiyor, usage toplamları saymıyor, kural
  // tetikleyicileri çalışmıyordu.
  // ŞUBE seviyesindeki dizi olmalı. Belge İÇİNDEKİ bir diziye ekleme
  // (students/{id}/allergies) buraya girmemeli — orada belgenin
  // tamamı aşağıda yazılıyor.
  if (mutasyon.op === "push" && koleksiyon === "branches" && kalan.length === 1) {
    const hedef = kalan[0];
    const yeni = mutasyon.after;
    if (!BELGE_KOLEKSIYONLARI.has(hedef) || !yeni?.id) return null;
    return { yol: `${belgeYolu}/${hedef}/${yeni.id}`, veri: kopya(yeni) };
  }

  const belge = getAt(kok, mutasyon.path.slice(0, parcalar.length * 2));
  if (!belge || typeof belge !== "object") return null;

  if (BELGE_KOLEKSIYONLARI.has(koleksiyon)) {
    return { yol: belgeYolu, veri: kopya(belge) };
  }

  // Yoklama: bellekte attendanceLog[gun][studentId], Firestore'da
  // attendance/{gun}/records/{sid}.
  //
  // Sunucunun yoklamayı OKUYABİLMESİ gerekiyor: "09:30'da gelmemiş
  // çocuk" gibi zamanlanmış kontroller belgeye bakıyor, mutasyon
  // günlüğüne değil.
  if (koleksiyon === "branches" && kalan[0] === "attendanceLog") {
    const [, gun, studentId] = kalan;
    if (!gun || !studentId) return null;

    const kayit = getAt(kok, mutasyon.path);
    if (mutasyon.op === "delete" || kayit == null) {
      return { yol: `${belgeYolu}/attendance/${gun}/records/${studentId}`, sil: true };
    }

    // classId belgeye YAZILMALI: firestore.rules eğitmenin yalnızca
    // kendi sınıfının yoklamasını yazabilmesini
    // request.resource.data.classId üzerinden denetliyor. Alan
    // olmazsa eğitmenin her yazması reddedilir.
    const ogrenci = (belge.students ?? []).find(x => x.id === studentId);

    return {
      yol: `${belgeYolu}/attendance/${gun}/records/${studentId}`,
      veri: kopya({ ...kayit, studentId, gun, classId: ogrenci?.classId ?? null }),
    };
  }

  // Şube: yalnızca skaler alanlar, yalnızca onlardan biri değiştiyse.
  if (koleksiyon === "branches") {
    const alan = kalan[0];
    if (kalan.length && !SUBE_ALANLARI.includes(alan)) return null;
    const veri = {};
    for (const a of SUBE_ALANLARI) if (belge[a] !== undefined) veri[a] = belge[a];
    return { yol: belgeYolu, veri, birlestir: true };
  }

  return null;
}

// Bellekteki nesne sonradan değişebilir; yazılacak kopyayı donduruyoruz.
// undefined alanlar Firestore'da hata verir, ayıklanıyor.
function kopya(v) {
  return JSON.parse(JSON.stringify(v, (_, x) => (x === undefined ? null : x)));
}
